import { useIdentity } from "../../provider-portal/IdentityProvider";
import { useFeatureGate } from "@statsig/react-bindings";

export const RITA_AND_SUZANNA_PROVIDER_IDS = [
  // Rita
  "8b77d3bb-6592-4cfc-812d-26e46679d33f",
  // Suzanna
  "2d4c2286-6b48-48f5-a437-67ed6b0b317c",
  // Joey's Dev Stack Test Provider
  "3634fe3f-184b-46e2-ab9b-723c150f0402",
];

export const INTERNAL_PROVIDER_IDS = [
  // Hessam
  "65791673-9559-4359-a1a0-2194e6f1c7de",
  // Anna
  "77eedb5c-1e94-4e69-a413-366877e52f19",
  // David
  "3113f7d5-1563-453c-a9ea-9a7d8e533a80",
];

const FEATURE_FLAG_CONFIG = {
  NUTRITION_RESEARCH_STREAMING: {
    default: {
      type: "boolean",
      value: false,
    } satisfies FeatureFlagValue,
  },
  CHART_NOTE_SCRIBE: {
    default: {
      type: "boolean",
      value: false,
    } satisfies FeatureFlagValue,
  },
  FAY_APPOINTMENT_LINKS: {
    default: {
      type: "boolean",
      value: false,
    } satisfies FeatureFlagValue,
  },
  VIRTUALIZE_CLIENT_LIST: {
    default: {
      type: "boolean",
      value: false,
    } satisfies FeatureFlagValue,
  },
};

export type FeatureFlagName = keyof typeof FEATURE_FLAG_CONFIG;

const isFeatureFlagName = (key: string): key is FeatureFlagName => {
  return Object.keys(FEATURE_FLAG_CONFIG).includes(key);
};

type FeatureFlagValue = { type: "boolean"; value: boolean };

type FeatureFlags = Record<FeatureFlagName, FeatureFlagValue>;

export const useChartNoteScribeFeatureFlag = () => {
  return useFeatureGate("chart_note_scribe").value;
};

export const useInPersonAvailabilityFeatureFlag = (): boolean => {
  return useFeatureGate("in_person_availability").value;
};

export const useFsaHsaEnabledFeatureFlag = (): boolean => {
  return useFeatureGate("display_fsa_and_hsa_selectors_for_payment_instruments")
    .value;
};

export const useAsyncBillingFeatureFlag = (): boolean => {
  return useFeatureGate("async_billing_frontend").value;
};

export const usePrototypeMarketplaceFeatureFlag = (): boolean => {
  return useFeatureGate("prototype_marketplace").value;
};

export const usePrototypeMarketplaceForAlphaProvidersFeatureFlag =
  (): boolean => {
    return useFeatureGate("prototype_marketplace_for_alpha_providers").value;
  };

type ProviderAwareOverrides = {
  enabledForAdminProviders?: boolean;
  enabledForBetaProviders?: boolean;
  enabledForInternalProviders?: boolean;
};

export const useProviderAwareFeatureFlags = ({
  overrides,
}: {
  overrides: Partial<Record<FeatureFlagName, ProviderAwareOverrides>>;
}) => {
  const { isAdmin, providerId } = useIdentity();

  const newOverrides: Partial<FeatureFlags> = Object.keys(overrides).reduce(
    (acc, _flag) => {
      const flag = _flag as FeatureFlagName;
      const flagOverrides = overrides[flag];
      if (flagOverrides) {
        if (flagOverrides.enabledForAdminProviders && isAdmin) {
          acc[flag] = {
            type: "boolean",
            value: true,
          };
        }
        if (
          flagOverrides.enabledForBetaProviders &&
          RITA_AND_SUZANNA_PROVIDER_IDS.includes(providerId ?? "")
        ) {
          acc[flag] = {
            type: "boolean",
            value: true,
          };
        }
        if (
          flagOverrides.enabledForInternalProviders &&
          INTERNAL_PROVIDER_IDS.includes(providerId ?? "")
        ) {
          acc[flag] = {
            type: "boolean",
            value: true,
          };
        }
      }
      return acc;
    },
    {} as Partial<FeatureFlags>,
  );

  return useFeatureFlags({
    overrides: newOverrides,
  });
};

export const useFeatureFlags = ({
  overrides,
}: {
  overrides: Partial<FeatureFlags>;
}): FeatureFlags => {
  const urlParams = new URLSearchParams(window.location.search);
  const urlParamFeatureFlags = Array.from(urlParams.keys()).filter((key) =>
    isFeatureFlagName(key),
  );
  const featureFlags = {
    // Defaults
    ...(Object.keys(FEATURE_FLAG_CONFIG).reduce(
      (acc, flag: string) => {
        if (!isFeatureFlagName(flag)) {
          return acc;
        }
        acc[flag] = FEATURE_FLAG_CONFIG[flag].default;
        return acc;
      },
      {} as Record<FeatureFlagName, FeatureFlagValue>,
    ) as FeatureFlags),
    // Overrides
    ...(Object.keys(overrides).reduce(
      (acc, flag: string) => {
        if (!isFeatureFlagName(flag)) {
          return acc;
        }
        const flagValue = overrides[flag];
        if (flagValue) {
          acc[flag] = flagValue;
        }
        return acc;
      },
      {} as Record<FeatureFlagName, FeatureFlagValue>,
    ) as FeatureFlags),
    // URL query parameters
    ...(urlParamFeatureFlags.reduce(
      (acc, key) => {
        if (!isFeatureFlagName(key)) {
          return acc;
        }
        if (urlParams.get(key) === "true") {
          acc[key] = { type: "boolean", value: true };
        }
        return acc;
      },
      {} as Record<FeatureFlagName, FeatureFlagValue>,
    ) as FeatureFlags),
  };

  return featureFlags;
};
